import api from "../api/API";

const CACHE_AUTH = "levelup-auth";
const CHAVE_TOKEN = "/__auth_token";

function base64ParaUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalizado = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const bruto = window.atob(normalizado);
  const buffer = new Uint8Array(bruto.length);

  for (let i = 0; i < bruto.length; i += 1) {
    buffer[i] = bruto.charCodeAt(i);
  }

  return buffer;
}

export function pushSuportado() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function ehIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function estaInstaladoNaHome() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

/**
 * No iOS o push so existe para PWA adicionado a tela de inicio. Sem isso o
 * navegador nem expoe o PushManager, entao vale avisar em vez de falhar mudo.
 */
export function precisaInstalarParaNotificar() {
  return ehIos() && !estaInstaladoNaHome();
}

export function permissaoAtual() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

/**
 * O service worker nao enxerga localStorage. Guardamos o JWT no Cache API
 * (mesma origem, mesmo nivel de exposicao) para as acoes "Feito" e "Adiar 1h"
 * da notificacao funcionarem sem abrir o app.
 */
export async function sincronizarTokenComServiceWorker() {
  if (!("caches" in window)) return;

  try {
    const cache = await caches.open(CACHE_AUTH);
    const token = localStorage.getItem("authToken");

    if (token) {
      await cache.put(CHAVE_TOKEN, new Response(token));
    } else {
      await cache.delete(CHAVE_TOKEN);
    }
  } catch (err) {
    console.error("pushClient: erro ao sincronizar token com o service worker", err);
  }
}

export async function limparTokenDoServiceWorker() {
  if (!("caches" in window)) return;
  try {
    await caches.delete(CACHE_AUTH);
  } catch (err) {
    console.error("pushClient: erro ao limpar token", err);
  }
}

async function obterChavePublica() {
  const resposta = await api.get("/push/chave-publica");

  if (!resposta?.data?.habilitado || !resposta?.data?.chave_publica) {
    throw new Error(
      "As notificacoes ainda nao estao configuradas no servidor (chaves VAPID ausentes)."
    );
  }

  return resposta.data.chave_publica;
}

/**
 * `navigator.serviceWorker.ready` fica pendente para sempre quando nenhum SW
 * foi registrado. Perguntar antes evita que qualquer chamada daqui trave.
 */
async function obterRegistration() {
  if (!("serviceWorker" in navigator)) return null;

  try {
    const existente = await navigator.serviceWorker.getRegistration();
    if (!existente) return null;
    return await navigator.serviceWorker.ready;
  } catch (err) {
    return null;
  }
}

export async function jaInscrito() {
  if (!pushSuportado()) return false;

  try {
    const registration = await obterRegistration();
    if (!registration) return false;

    const subscription = await registration.pushManager.getSubscription();
    return Boolean(subscription);
  } catch (err) {
    return false;
  }
}

/** Endpoint deste navegador, para saber qual dos dispositivos da lista e ele. */
export async function endpointDesteAparelho() {
  try {
    const registration = await obterRegistration();
    if (!registration) return null;

    const subscription = await registration.pushManager.getSubscription();
    return subscription?.endpoint || null;
  } catch (err) {
    return null;
  }
}

export async function listarDispositivos() {
  const resposta = await api.get("/push/dispositivos");
  return resposta?.data?.records || [];
}

/**
 * A inscricao fica amarrada a chave VAPID usada na criacao. Se as chaves do
 * servidor mudaram depois, todo envio para ela volta 403 e o navegador nunca
 * toca - sem nenhum sinal para o usuario. Comparar as chaves e o que permite
 * refazer a inscricao em vez de esperar por uma notificacao que nao vem.
 */
function mesmaChaveVapid(subscription, chavePublicaBase64) {
  const atual = subscription?.options?.applicationServerKey;
  // Navegador que nao expoe a chave nao da base para julgar; mantemos a inscricao.
  if (!atual) return true;

  const esperada = base64ParaUint8Array(chavePublicaBase64);
  const bytes = new Uint8Array(atual);

  if (bytes.length !== esperada.length) return false;
  return bytes.every((valor, indice) => valor === esperada[indice]);
}

/**
 * Reconcilia a inscricao deste navegador com o servidor, sem pedir nada ao
 * usuario.
 *
 * Existir uma inscricao no navegador NAO significa que o servidor sabe dela: o
 * Chrome desktop rotaciona a inscricao sozinho (atualizacao do navegador, perfil
 * reciclado, longa inatividade) e o endpoint antigo passa a responder 410 - que
 * o servidor apaga, com razao. Sem esta reconciliacao aquele navegador ficava
 * mudo para sempre, enquanto o celular continuava recebendo.
 *
 * Como a permissao ja foi concedida, recriar a inscricao aqui nao precisa de
 * gesto do usuario. O POST e sempre reenviado porque o servidor faz upsert por
 * endpoint: e o que ressuscita uma inscricao que sumiu do banco.
 */
export async function garantirInscricaoSincronizada() {
  if (!pushSuportado()) return { ok: false, motivo: "sem-suporte" };
  if (Notification.permission !== "granted") return { ok: false, motivo: "sem-permissao" };

  const registration = await obterRegistration();
  if (!registration) return { ok: false, motivo: "sem-service-worker" };

  try {
    const chavePublica = await obterChavePublica();
    let subscription = await registration.pushManager.getSubscription();
    let recriada = false;

    if (subscription && !mesmaChaveVapid(subscription, chavePublica)) {
      await subscription.unsubscribe().catch(() => {});
      subscription = null;
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64ParaUint8Array(chavePublica),
      });
      recriada = true;
    }

    await api.post("/push/subscribe", { subscription: subscription.toJSON() });
    await sincronizarTokenComServiceWorker();

    return { ok: true, recriada, endpoint: subscription.endpoint };
  } catch (err) {
    console.error("pushClient: falha ao sincronizar a inscricao com o servidor", err);
    return { ok: false, motivo: "erro", erro: err };
  }
}

/**
 * Precisa ser chamada a partir de um gesto do usuario - no iOS e obrigatorio,
 * e nos demais navegadores melhora muito a taxa de aceite.
 */
export async function ativarNotificacoes() {
  if (!pushSuportado()) {
    if (precisaInstalarParaNotificar()) {
      throw new Error(
        "No iPhone, adicione o LevelUp a tela de inicio primeiro. So assim o iOS libera notificacoes."
      );
    }
    throw new Error("Este navegador nao suporta notificacoes push.");
  }

  const permissao = await Notification.requestPermission();

  if (permissao !== "granted") {
    throw new Error("Permissao de notificacao negada.");
  }

  const chavePublica = await obterChavePublica();
  const registration = await obterRegistration();

  if (!registration) {
    throw new Error(
      "O service worker ainda nao esta pronto neste navegador. Recarregue a pagina e tente de novo."
    );
  }

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64ParaUint8Array(chavePublica),
    });
  }

  await api.post("/push/subscribe", { subscription: subscription.toJSON() });
  await sincronizarTokenComServiceWorker();

  return true;
}

export async function desativarNotificacoes() {
  if (!pushSuportado()) return false;

  const registration = await obterRegistration();
  if (!registration) return false;

  const subscription = await registration.pushManager.getSubscription();

  if (!subscription) return false;

  await api
    .delete("/push/subscribe", { data: { endpoint: subscription.endpoint } })
    .catch(() => {});

  await subscription.unsubscribe();
  return true;
}

export async function enviarNotificacaoTeste() {
  const resposta = await api.post("/lembretes/teste-notificacao");
  return resposta?.data;
}
